import { useState } from "react";
import { Check } from "lucide-react";
import { Link } from "react-router-dom";
import { mailtoConsult } from "@/lib/contact";
import { scrollToId } from "@/lib/scroll";
import {
    carePlans,
    comparisonRows,
    costDrivers,
    pricingFaq,
    pricingSteps,
    proposalBudgets,
    proposalIndustries,
    websitePackages,
} from "@/lib/offers";

const inputCls =
    "w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-sm text-[#0a0a0b] outline-none focus:border-[var(--brand)]/60 focus:ring-2 focus:ring-[var(--brand)]/20";

export default function PricingPage() {
    const [pack, setPack] = useState("");
    const [sent, setSent] = useState(false);

    const choose = (id) => {
        setPack(id);
        scrollToId("#proposal");
    };

    const submit = (event) => {
        event.preventDefault();
        const data = new FormData(event.currentTarget);
        const body = [
            `Name: ${data.get("name")}`,
            `Company: ${data.get("company")}`,
            `Email: ${data.get("email")}`,
            `Phone: ${data.get("phone") || "—"}`,
            `Industry: ${data.get("industry")}`,
            `Website: ${data.get("website") || "—"}`,
            `Package: ${data.get("package")}`,
            `Budget: ${data.get("budget") || "—"}`,
            `Project: ${data.get("description")}`,
        ].join("\n");
        window.location.href = `${mailtoConsult}&body=${encodeURIComponent(body)}`;
        setSent(true);
    };

    return (
        <main data-testid="pricing-page" className="bg-white text-[#0a0a0b]">
            <section className="bg-lavender px-5 pb-16 pt-36 sm:pb-20 sm:pt-44">
                <div className="mx-auto max-w-4xl text-center">
                    <h1 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-6xl">
                        Transparent Pricing For
                        <br />
                        AI-Powered Websites &amp; Business Automation
                    </h1>
                    <p className="mx-auto mt-5 max-w-2xl text-[15px] leading-relaxed text-[#5b5b66] sm:text-lg">
                        Websites, AI chatbots, CRM automation, lead capture, and business workflows designed to help
                        Canadian businesses grow.
                    </p>
                    <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                        <button type="button" onClick={() => scrollToId("#proposal")} className="rounded-full bg-[var(--brand)] px-6 py-3 text-sm font-semibold text-white shadow-[0_12px_28px_rgba(43,80,255,0.35)]">
                            Book a Free Consultation
                        </button>
                        <button type="button" onClick={() => scrollToId("#proposal")} className="rounded-full bg-white px-6 py-3 text-sm font-semibold text-[#0a0a0b] ring-1 ring-black/10">
                            Request Custom Quote
                        </button>
                    </div>
                    <div className="mt-8 flex flex-wrap items-center justify-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#5b5b66]">
                        {["Built in Canada", "AI Powered", "No Long-Term Contracts", "Founder-Led Delivery"].map((item) => (
                            <span key={item} className="rounded-full bg-white px-3 py-1.5 ring-1 ring-black/5">
                                {item}
                            </span>
                        ))}
                    </div>
                </div>
            </section>

            <section className="px-5 py-16 sm:py-20">
                <div className="mx-auto max-w-6xl">
                    <div className="mb-10 text-center">
                        <h2 className="font-display text-3xl font-extrabold sm:text-4xl">Choose Your Package</h2>
                        <p className="mt-2 text-[#5b5b66]">Four ways to work with SalesCan</p>
                    </div>
                    <div className="grid gap-5 lg:grid-cols-4">
                        {websitePackages.map((item) => (
                            <article
                                key={item.id}
                                id={item.id}
                                className={`relative flex h-full flex-col rounded-3xl p-6 ring-1 ${
                                    item.popular
                                        ? "bg-[#0a0a0b] text-white ring-[#0a0a0b]"
                                        : "bg-[#f6f7fb] text-[#0a0a0b] ring-black/5"
                                }`}
                            >
                                {item.popular ? (
                                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[var(--brand)] px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-white">
                                        Most Popular
                                    </span>
                                ) : null}
                                <h3 className="font-display text-xl font-extrabold">{item.name}</h3>
                                <p className="mt-3 font-display text-4xl font-extrabold tracking-tight">{item.price}</p>
                                <p className={`text-sm ${item.popular ? "text-white/70" : "text-[#8a8a95]"}`}>{item.cadence}</p>
                                <p className={`mt-1 text-sm font-semibold ${item.popular ? "text-white" : "text-[#0a0a0b]"}`}>
                                    {item.monthly}
                                </p>
                                <p className={`mt-4 text-sm leading-relaxed ${item.popular ? "text-white/75" : "text-[#5b5b66]"}`}>
                                    {item.audience}
                                </p>
                                <ul className="mt-5 flex-1 space-y-2">
                                    {item.points.map((point) => (
                                        <li key={point.label} className={`flex items-start gap-2 text-sm ${point.on ? "" : "opacity-45"}`}>
                                            <span className="mt-0.5 w-4 shrink-0">{point.on ? "✓" : "—"}</span>
                                            <span>{point.label}</span>
                                        </li>
                                    ))}
                                </ul>
                                <button
                                    type="button"
                                    onClick={() => choose(item.name)}
                                    className={`mt-6 rounded-full px-4 py-2.5 text-sm font-semibold ${
                                        item.popular ? "bg-white text-[#0a0a0b]" : "bg-[var(--brand)] text-white"
                                    }`}
                                >
                                    {item.cta}
                                </button>
                            </article>
                        ))}
                    </div>
                    <p className="mx-auto mt-8 max-w-3xl text-center text-sm leading-relaxed text-[#5b5b66]">
                        Every package is fully owned by you, with no lock-in. Prices in CAD; applicable taxes extra.
                    </p>
                    <div id="marketing" className="mx-auto mt-6 max-w-3xl rounded-3xl bg-[var(--brand-soft)] px-6 py-5 text-center">
                        <p className="font-display text-lg font-bold">Want ongoing growth, not just a website?</p>
                        <p className="mt-2 text-sm leading-relaxed text-[#5b5b66]">
                            Monthly digital marketing and SEO retainers — ads, SEO, content and AI — from $1,999/mo.
                        </p>
                    </div>
                </div>
            </section>

            <section className="bg-[#f6f7fb] px-5 py-16 sm:py-20">
                <div className="mx-auto max-w-6xl">
                    <h2 className="font-display text-3xl font-extrabold sm:text-4xl">What affects the cost of a website</h2>
                    <p className="mt-3 max-w-3xl text-[15px] leading-relaxed text-[#5b5b66]">
                        The packages above are fixed-scope. Across Canada, a website quote mostly moves with the factors
                        below, which we settle in writing before any build starts.
                    </p>
                    <div className="mt-8 grid gap-4 md:grid-cols-3">
                        {costDrivers.map((item) => (
                            <article key={item.title} className="rounded-3xl bg-white p-6 ring-1 ring-black/5">
                                <h3 className="font-display text-lg font-bold">{item.title}</h3>
                                <p className="mt-2 text-sm leading-relaxed text-[#5b5b66]">{item.body}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <section className="px-5 py-16 sm:py-20">
                <div className="mx-auto max-w-6xl">
                    <h2 className="font-display text-3xl font-extrabold sm:text-4xl">Full Comparison</h2>
                    <p className="mt-2 text-[#5b5b66]">Every feature, side by side — so you can choose with confidence.</p>
                    <div className="mt-8 overflow-x-auto rounded-3xl ring-1 ring-black/5">
                        <table className="w-full min-w-[760px] border-collapse text-left text-sm">
                            <thead className="bg-[#0a0a0b] text-white">
                                <tr>
                                    <th className="px-4 py-3 font-semibold">Feature</th>
                                    {websitePackages.map((item) => (
                                        <th key={item.id} className="px-4 py-3 font-semibold">
                                            {item.name}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {comparisonRows.map((row, index) => (
                                    <tr key={row.feature} className={index % 2 ? "bg-[#f6f7fb]" : "bg-white"}>
                                        <th className="px-4 py-3 font-semibold text-[#0a0a0b]">{row.feature}</th>
                                        {row.values.map((value, valueIndex) => (
                                            <td key={`${row.feature}-${valueIndex}`} className="px-4 py-3 text-[#5b5b66]">
                                                {value}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>

            <section className="bg-[#f6f7fb] px-5 py-16 sm:py-20">
                <div className="mx-auto max-w-6xl">
                    <h2 className="font-display text-3xl font-extrabold sm:text-4xl">How We Work</h2>
                    <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        {pricingSteps.map((step, index) => (
                            <article key={step.title} className="rounded-3xl bg-white p-6 ring-1 ring-black/5">
                                <p className="font-display text-3xl font-extrabold text-[var(--brand)]">{index + 1}</p>
                                <h3 className="mt-3 font-display text-lg font-bold">{step.title}</h3>
                                <p className="mt-2 text-sm leading-relaxed text-[#5b5b66]">{step.body}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <section className="px-5 py-16 sm:py-20">
                <div className="mx-auto max-w-6xl">
                    <h2 className="font-display text-3xl font-extrabold sm:text-4xl">Monthly Care Plans</h2>
                    <p className="mt-2 text-[#5b5b66]">Keep your site fast, secure, and converting after launch.</p>
                    <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        {carePlans.map((plan) => (
                            <article key={plan.name} className="flex h-full flex-col rounded-3xl bg-[#f6f7fb] p-6 ring-1 ring-black/5">
                                <h3 className="font-display text-lg font-bold">{plan.name}</h3>
                                <p className="mt-2 font-display text-3xl font-extrabold">
                                    {plan.price}
                                    <span className="text-base font-semibold text-[#8a8a95]">{plan.cadence}</span>
                                </p>
                                <ul className="mt-4 flex-1 space-y-2 text-sm text-[#5b5b66]">
                                    {plan.points.map((point) => (
                                        <li key={point} className="flex gap-2">
                                            <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#15803d]" />
                                            {point}
                                        </li>
                                    ))}
                                </ul>
                                <button
                                    type="button"
                                    onClick={() => choose(plan.name)}
                                    className="mt-6 text-left text-sm font-semibold text-[var(--brand)]"
                                >
                                    Request a proposal →
                                </button>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <section id="proposal" className="bg-[#f6f7fb] px-5 py-16 sm:py-20">
                <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-[0.8fr_1.2fr]">
                    <div>
                        <h2 className="font-display text-3xl font-extrabold sm:text-4xl">Request a proposal</h2>
                        <p className="mt-3 text-[15px] leading-relaxed text-[#5b5b66]">
                            Tell us about your project. Share a few details and we&apos;ll send a tailored proposal within
                            1 business day.
                        </p>
                    </div>
                    {sent ? (
                        <div className="rounded-3xl bg-white p-8 ring-1 ring-black/5">
                            <h3 className="font-display text-2xl font-bold">Your email app should open</h3>
                            <p className="mt-2 text-sm text-[#5b5b66]">Send that message and we&apos;ll reply with the proposal.</p>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="grid gap-4 rounded-3xl bg-white p-6 ring-1 ring-black/5 sm:grid-cols-2 sm:p-8">
                            <label className="text-sm font-medium">
                                Full Name *
                                <input name="name" required className={`${inputCls} mt-1.5`} />
                            </label>
                            <label className="text-sm font-medium">
                                Company Name *
                                <input name="company" required className={`${inputCls} mt-1.5`} />
                            </label>
                            <label className="text-sm font-medium">
                                Email *
                                <input name="email" type="email" required className={`${inputCls} mt-1.5`} />
                            </label>
                            <label className="text-sm font-medium">
                                Phone
                                <input name="phone" className={`${inputCls} mt-1.5`} />
                            </label>
                            <label className="text-sm font-medium">
                                Industry
                                <select name="industry" className={`${inputCls} mt-1.5`} defaultValue="">
                                    <option value="" disabled>
                                        Select industry
                                    </option>
                                    {proposalIndustries.map((item) => (
                                        <option key={item}>{item}</option>
                                    ))}
                                </select>
                            </label>
                            <label className="text-sm font-medium">
                                Website URL
                                <input name="website" className={`${inputCls} mt-1.5`} />
                            </label>
                            <label className="text-sm font-medium">
                                Package Interested In *
                                <select name="package" required className={`${inputCls} mt-1.5`} value={pack} onChange={(event) => setPack(event.target.value)}>
                                    <option value="" disabled>
                                        Select a package
                                    </option>
                                    {websitePackages.map((item) => (
                                        <option key={item.id}>{item.name}</option>
                                    ))}
                                    <option>Not sure yet</option>
                                </select>
                            </label>
                            <label className="text-sm font-medium">
                                Project Budget
                                <select name="budget" className={`${inputCls} mt-1.5`} defaultValue="">
                                    <option value="" disabled>
                                        Select budget
                                    </option>
                                    {proposalBudgets.map((item) => (
                                        <option key={item}>{item}</option>
                                    ))}
                                </select>
                            </label>
                            <label className="text-sm font-medium sm:col-span-2">
                                Project Description *
                                <textarea name="description" required rows={4} className={`${inputCls} mt-1.5`} />
                            </label>
                            <div className="sm:col-span-2">
                                <label className="flex items-start gap-2 text-sm leading-relaxed text-[#5b5b66]">
                                    <input name="consent" type="checkbox" required className="mt-1" />
                                    I consent to SalesCan contacting me about this inquiry. I can unsubscribe at any time.
                                </label>
                                <p className="mt-2 text-xs leading-relaxed text-[#8a8a95]">
                                    See the{" "}
                                    <Link to="/privacy" className="font-semibold text-[#0a0a0b] underline underline-offset-4">
                                        Privacy
                                    </Link>
                                    {" "}and{" "}
                                    <Link to="/terms" className="font-semibold text-[#0a0a0b] underline underline-offset-4">
                                        Terms
                                    </Link>
                                    {" "}notices.
                                </p>
                            </div>
                            <button type="submit" className="rounded-full bg-[var(--brand)] px-6 py-3 text-sm font-semibold text-white shadow-[0_12px_28px_rgba(43,80,255,0.35)] sm:col-span-2 sm:w-fit">
                                Request Proposal
                            </button>
                        </form>
                    )}
                </div>
            </section>

            <section className="px-5 py-16 sm:py-20">
                <div className="mx-auto max-w-3xl">
                    <h2 className="font-display text-3xl font-extrabold sm:text-4xl">Frequently Asked Questions</h2>
                    <div className="mt-8 space-y-3">
                        {pricingFaq.map((item) => (
                            <details key={item.q} className="rounded-2xl bg-[#f6f7fb] p-5 ring-1 ring-black/5">
                                <summary className="cursor-pointer font-semibold">{item.q}</summary>
                                <p className="mt-2 text-sm leading-relaxed text-[#5b5b66]">{item.a}</p>
                            </details>
                        ))}
                    </div>
                </div>
            </section>

            <section className="px-5 pb-20">
                <div className="mx-auto max-w-4xl rounded-3xl bg-[#0a0a0b] px-6 py-12 text-center text-white sm:px-10">
                    <h2 className="font-display text-3xl font-extrabold sm:text-4xl">Tell us what you want built.</h2>
                    <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-white/70">
                        A fixed price before anyone starts. We reply from St. Catharines.
                    </p>
                    <button type="button" onClick={() => scrollToId("#proposal")} className="mt-6 rounded-full bg-[var(--brand)] px-6 py-3 text-sm font-semibold text-white shadow-[0_12px_28px_rgba(43,80,255,0.35)]">
                        Request a proposal
                    </button>
                </div>
            </section>
        </main>
    );
}
